
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { createDonation } from '../../api/apiServices';
import { useNavigate } from 'react-router-dom';

export default function DonatePage() {
  const navigate = useNavigate();
  const [selectedAmount, setSelectedAmount] = useState(50000);
  const [customAmount, setCustomAmount] = useState('');
  const [errors, setErrors] = useState({});
  const [donationType, setDonationType] = useState('one-time');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');

  const amounts = [50000, 100000, 200000, 300000, 500000, 1000000];

  const validateForm = () => {
  const newErrors = {};

  const amount = customAmount || selectedAmount;

  if (!amount || Number(amount) <= 0) {
    newErrors.amount = "Please select or enter a valid donation amount.";
  }

  if (!formData.firstName.trim()) {
    newErrors.firstName = "First name is required.";
  }

  if (!formData.lastName.trim()) {
    newErrors.lastName = "Last name is required.";
  }

  if (!formData.email.trim()) {
    newErrors.email = "Email is required.";
  } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
    newErrors.email = "Please enter a valid email address.";
  }

  if (!formData.phone.trim()) {
    newErrors.phone = "Phone number is required.";
  }

  if (!formData.message.trim()) {
    newErrors.message = "Message is required.";
  }

  setErrors(newErrors);

  return Object.keys(newErrors).length === 0;
};


  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validateForm()) return;

  try {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const res = await createDonation({
      donationType,
      amount: customAmount || selectedAmount,
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      email: formData.email,
      message: formData.message,
    });

    const donation = res.data
    navigate(`/donation/payment/${donation._id}`, { 
      state: {
        donation
      }
    })
  } catch (error) {
    console.error("Donation failed:", error);
    setSubmitStatus("error");
  } finally {
    setIsSubmitting(false);
  }
};


  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-20 sm:pt-24">
        <div className="relative h-[350px] sm:h-[400px]">
          <img
            src="https://readdy.ai/api/search-image?query=compassionate%20hands%20gently%20holding%20and%20supporting%20another%20person%20hands%2C%20warm%20golden%20sunlight%20filtering%20through%2C%20simple%20soft%20blurred%20background%2C%20showing%20care%20and%20human%20connection%2C%20emotional%20support%20and%20kindness&width=1920&height=500&seq=donate-hero-001&orientation=landscape"
            alt="Make a Difference"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#6a4bc3]/90 to-[#6a4bc3]/70"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-20 w-full">
              <p className="text-white/80 text-sm mb-3">MAKE A DIFFERENCE</p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                Transform Lives<br />Through Giving
              </h1>
              <p className="text-white/90 text-base sm:text-lg max-w-xl">
                Your donation provides critical support to survivors of kidnapping and crisis situations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Form Section */}
<section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-20 bg-[#f8f6fc]">
  <div className="max-w-7xl mx-auto">
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">

      {/* Left – Donation Info / Impact */}
      <div className="lg:sticky lg:top-24">
        <p className="text-[#c9a24d] text-sm font-semibold mb-3">
          MAKE A DIFFERENCE
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
          Your Support Changes Lives
        </h2>
        <p className="text-gray-700 leading-relaxed mb-6">
          Every donation helps us provide counseling, legal support, and
          long-term recovery programs for survivors. Your generosity restores
          hope and rebuilds futures.
        </p>

        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full bg-[#6a4bc3]/10 flex items-center justify-center">
              <i className="ri-heart-line text-[#6a4bc3]"></i>
            </div>
            <p className="text-sm text-gray-600">
              Trauma-informed counseling and recovery care
            </p>
          </div>

          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full bg-[#6a4bc3]/10 flex items-center justify-center">
              <i className="ri-shield-line text-[#6a4bc3]"></i>
            </div>
            <p className="text-sm text-gray-600">
              Legal advocacy and survivor protection
            </p>
          </div>

          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full bg-[#6a4bc3]/10 flex items-center justify-center">
              <i className="ri-community-line text-[#6a4bc3]"></i>
            </div>
            <p className="text-sm text-gray-600">
              Community reintegration and job training
            </p>
          </div>
        </div>
      </div>

      {/* Right – Donation Form */}
      <div>
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10 border border-gray-100">
          <form
            id="donation-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            {/* Donation Type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Donation Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setDonationType('one-time')}
                  className={`py-3 rounded-lg font-medium transition-all cursor-pointer ${
                    donationType === 'one-time'
                      ? 'bg-[#6a4bc3] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  One-Time
                </button>
                <button
                  type="button"
                  onClick={() => setDonationType('monthly')}
                  className={`py-3 rounded-lg font-medium transition-all cursor-pointer ${
                    donationType === 'monthly'
                      ? 'bg-[#6a4bc3] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Monthly
                </button>
              </div>
            </div>

            {/* Amount Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Select Amount
              </label>
              <div className="grid grid-cols-3 gap-3 mb-3">
                {amounts.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => {
                      setSelectedAmount(amount);
                      setCustomAmount('');
                    }}
                    className={`py-3 rounded-lg font-semibold transition-all cursor-pointer ${
                      selectedAmount === amount && !customAmount
                        ? 'bg-[#c9a24d] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    N{amount.toLocaleString()}
                  </button>
                ))}
              </div>

              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                  N
                </span>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setSelectedAmount(null);
                  }}
                  placeholder="Custom amount"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6a4bc3] text-sm"
                />
              </div>
            </div>

            {/* Personal Information */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6a4bc3] text-sm"
                      />
                      {errors.firstName && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.firstName}
                          </p>
                        )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6a4bc3] text-sm"
                      />
                      {errors.lastName && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.lastName}
                          </p>
                        )}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6a4bc3] text-sm"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.email}
                          </p>
                        )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6a4bc3] text-sm"
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.phone}
                          </p>
                        )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setFormData({ ...formData, message: e.target.value });
                        }
                      }}
                      rows={3}
                      maxLength={500}
                      required
                      placeholder="Leave a message with your donation..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6a4bc3] text-sm resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                    {errors.message && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.message}
                          </p>
                        )}
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                      Thank you for your generous donation! You'll receive a confirmation email shortly.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                      Something went wrong. Please try again.
                    </div>
                  )}

            <button
              type="submit"
              disabled={isSubmitting || (!selectedAmount && !customAmount)}
              className="w-full bg-[#c9a24d] text-white py-4 rounded-full font-semibold text-lg hover:bg-[#b8923d] transition-colors disabled:opacity-50"
            >
              {isSubmitting
                ? 'Processing...'
                : `Donate N${customAmount || selectedAmount || 0}${
                    donationType === 'monthly' ? ' / month' : ''
                  }`}
            </button>

            <p className="text-center text-xs text-gray-500">
              Secure payment · Privacy protected
            </p>
          </form>
        </div>
      </div>

    </div>
  </div>
</section>


      <Footer />
    </div>
  );
}
